import { GridOption, CustomColumn, TreeGridOption } from '@/common/utils/types';
import { treeGridOption } from '@/common/utils/define';
import { i18n } from '@/common/locale';

export const baseGridOption: GridOption = {
  adjust: true,
  useFilter: false,
  rowMinHeight: 28,
  rowHeight: 28,
  scrollWidth: 8,
  useCheckbox: {
    use: false,
  },
  style: {
    border: 'all',
  },
  useGridSetting: {
    use: true,
  },
};

export const listViewGridOption: GridOption = {
  ...baseGridOption,
  rowMinHeight: 28,
  rowHeight: 28,
  useGridSetting: {
    use: true,
  },
  useSelection: {
    use: true,
    disableDeselect: true,
  },
  page: {
    use: true,
    useClient: false,
  },
  maintainScrollOnUpdateRows: true,
};

export const listViewConfigGridOption: GridOption = {
  ...listViewGridOption,
  useCheckbox: {
    use: true,
    mode: 'multi',
    headerCheck: true,
  },
  useGridSetting: {
    use: true,
  },
};

export const listViewSearchBarOption = {
  use: true,
  mode: 'full',
  useClientFilter: true,
} as const;

export const listViewSearchBarOptionWithUseClientFilterFalse = {
  ...listViewSearchBarOption,
  useClientFilter: false,
} as const;

export const listViewPagination = {
  use: true,
  showPageInfo: true,
  showSetting: true,
  useClient: false,
};

export const detailGridOption: GridOption = {
  ...baseGridOption,
  rowMinHeight: 28,
  rowHeight: 28,
  page: {
    use: true,
    useClient: false,
  },
};

export const eventGridColumns: CustomColumn[] = [
  {
    caption: 'Type',
    field: 'type',
    type: 'string',
    width: 120,
    searchable: true,
    rendererType: 'capitalize-label',
  },
  {
    caption: 'Create Time',
    field: 'createTime',
    type: 'string',
    width: 140,
    searchable: true,
  },
  {
    caption: 'Kind',
    field: 'kind',
    type: 'string',
    width: 180,
    searchable: true,
  },
  {
    caption: 'Name',
    field: 'name',
    type: 'string',
    width: 250,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Namespace',
    field: 'namespace',
    type: 'string',
    width: 200,
    searchable: true,
  },
  {
    caption: 'Reason',
    field: 'reason',
    type: 'string',
    width: 200,
    searchable: true,
  },
  {
    caption: 'Message',
    field: 'message',
    type: 'string',
    width: 500,
    searchable: true,
    clickable: true,
    defaultSearchField: true,
  },
];

export const containersGridColumns: CustomColumn[] = [
  {
    caption: 'Container Name',
    field: 'name',
    type: 'string',
    width: 250,
    clickable: true,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Status',
    field: 'state',
    type: 'string',
    width: 100,
    rendererType: 'capitalize-label',
    align: 'center',
    searchable: true,
  },
  {
    caption: 'Alert',
    field: 'level',
    type: 'string',
    width: 90,
    rendererType: 'capitalize-label',
    align: 'center',
    searchable: true,
  },
  {
    caption: 'vCore',
    field: 'vcore',
    type: 'number',
    width: 90,
    searchable: true,
  },
  {
    caption: 'CPU Limit',
    field: 'cpuLimit',
    type: 'number',
    width: 90,
    searchable: true,
  },
  {
    caption: 'CPU Usage/Limit (%)',
    field: 'cpuUsageByLimit',
    type: 'number',
    width: 145,
    rendererType: 'ratio',
    searchable: true,
  },
  {
    caption: 'CPU Usage/Node Alloc (%)',
    field: 'cpuUsageByNodeAlloc',
    type: 'number',
    width: 145,
    rendererType: 'ratio',
    searchable: true,
  },
  {
    caption: 'Memory Limit',
    field: 'memoryLimit',
    type: 'number',
    width: 90,
    searchable: true,
  },
  {
    caption: 'Mem Usage/Limit (%)',
    field: 'memoryUsageByLimit',
    type: 'number',
    width: 145,
    rendererType: 'ratio',
    searchable: true,
  },
  {
    caption: 'Mem Usage/Node Alloc (%)',
    field: 'memoryUsageByNodeAlloc',
    type: 'number',
    width: 145,
    rendererType: 'ratio',
    searchable: true,
  },
  {
    caption: 'RX',
    field: 'rx',
    type: 'number',
    width: 120,
    searchable: true,
    specificFilterFnKey: 'toBytes',
  },
  {
    caption: 'TX',
    field: 'tx',
    type: 'number',
    width: 120,
    searchable: true,
    specificFilterFnKey: 'toBytes',
  },
  {
    caption: 'Image',
    field: 'image',
    type: 'string',
    width: 180,
    searchable: true,
  },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'CPU Used (millicore)',
    field: 'cpuUsed',
    type: 'number',
    width: 100,
    hiddenDisplay: true,
    searchable: true,
  },
  {
    caption: 'CPU Share',
    field: 'cpuShares',
    type: 'number',
    width: 90,
    hiddenDisplay: true,
    searchable: true,
  },
  {
    caption: 'CPU Throttled Time',
    field: 'cpuThrottledTime',
    type: 'number',
    width: 120,
    hiddenDisplay: true,
    searchable: true,
    specificFilterFnKey: 'toMs',
  },
  {
    caption: 'CPU Throttled Periods',
    field: 'cpuThrottledPeriods',
    type: 'number',
    width: 120,
    hiddenDisplay: true,
    searchable: true,
  },
  {
    caption: 'Memory WorkingSet',
    field: 'memoryWorkingset',
    type: 'number',
    width: 160,
    hiddenDisplay: true,
    searchable: true,
    specificFilterFnKey: 'toBytes',
  },
  {
    caption: 'Memory RSS',
    field: 'memoryRss',
    type: 'number',
    width: 160,
    hiddenDisplay: true,
    searchable: true,
    specificFilterFnKey: 'toBytes',
  },
  {
    caption: 'Memory Swap Used',
    field: 'memorySwap',
    type: 'number',
    width: 160,
    hiddenDisplay: true,
    searchable: true,
    specificFilterFnKey: 'toBytes',
  },
  {
    caption: 'containerId',
    field: 'containerId',
    type: 'string',
    hide: true,
  },
];

export const gridSettingOption = {
  useGridSetting: {
    use: true,
    columnMenuText: i18n.global.t('WORD.COLUMN_LIST'),
    searchText: i18n.global.t('WORD.SEARCH'),
    emptyText: i18n.global.t('MESSAGE.NO_DATA'),
    okBtnText: i18n.global.t('WORD.OK'),
  },
  columnMenuText: {
    hide: i18n.global.t('WORD.HIDE'),
  },
  emptyText: i18n.global.t('MESSAGE.NO_DATA'),
};

export const paTreeGridOption: TreeGridOption = {
  adjust: true,
  showHeader: true,
  rowHeight: 28,
  parentIcon: 'none',
  childIcon: 'none',
  style: {
    highlight: -1,
  },
  useCheckbox: {
    use: false,
  },
  expandIcon: 'ev-icon-square-plus',
  collapseIcon: 'ev-icon-square-minus',
  useGridSetting: {
    use: true,
    columnMenuText: i18n.global.t('WORD.COLUMN_LIST'),
    searchText: i18n.global.t('WORD.SEARCH'),
    emptyText: i18n.global.t('MESSAGE.NO_DATA'),
    okBtnText: i18n.global.t('WORD.OK'),
  },
  columnMenuText: {
    hide: i18n.global.t('WORD.HIDE'),
  },
  emptyText: i18n.global.t('MESSAGE.NO_DATA'),
};

export const listViewTreeGridOption = {
  ...treeGridOption,
  rowHeight: 28,
  useGridSetting: {
    use: true,
  },
  useSelection: {
    use: true,
    disableDeselect: true,
  },
};

export const listViewConfigTreeGridOption: GridOption = {
  ...listViewTreeGridOption,
  useCheckbox: {
    use: true,
    headerCheck: true,
  },
};

export const ROW_DATA_INDEX = 2;
export const ROW_DISABLED_INDEX = 6;
